import { TEI_CONTENT_HEIGHT } from '@/lib/constants';
import { TABLE_TEI_CONTENT_HEIGHT } from '@/lib/constants/teis/table';

// Each subtype prints at the same box it uses in the full-slide asset.
const GO_HEIGHT_BY_SUBTYPE = { table: TABLE_TEI_CONTENT_HEIGHT };
export const printGoHeight = (subtype) => (
  GO_HEIGHT_BY_SUBTYPE[subtype] || TEI_CONTENT_HEIGHT
);

// Below this rendered size the GO can't share a sheet with instructions.
export const PRINT_GO_MIN_WIDTH = 628;
export const PRINT_GO_MIN_HEIGHT = 376;

// Height of one repeat of the K-2 blank triple-track handwriting-lines pattern.
export const PRINT_TRIPLE_TRACK_TILE_HEIGHT = 80;

// Subtype -> print content component, for full-slide TEIs that aren't GOs.
export const TEI_PRINT_COMPONENTS = {
  'constructed-response': 'TEIPrintConstructedResponse',
  connections: 'TEIPrintConnections',
  'select-object': 'TEIPrintSelectObject',
  'selected-response': 'TEIPrintSelectedResponse',
  tiling: 'TEIPrintTiling',
};

// Selected Response choice-card sizes.
export const SELECTED_RESPONSE_CARD_SIZE = {
  large: { width: 250, height: 171 },
  small: { width: 156, height: 107 },
};

// Choice-count -> row sizes for a chip/card grid that always keeps to 2 rows.
// Shared by Selected Response's image-card grid and Tiling's word bank.
export const CHOICE_GRID_ROW_SIZES = {
  2: [2],
  3: [3],
  4: [2, 2],
  5: [3, 2],
  6: [3, 3],
};

// Prompt image box height for Selected Response.
export const PROMPT_IMAGE_BOX_HEIGHT = 220;

// Prompt image box height for Tiling (Flow); Columns halves this.
export const TILING_PROMPT_IMAGE_BOX_HEIGHT = 184;

export const GRADE_BAND_K2 = 'k2';
export const GRADE_BAND_35 = '35';
// user_grade abbreviations that resolve to K-2; everything else is 3-5.
export const K2_GRADES = ['PreK', 'K', '1', '2'];

// Tiling flow blank width, per grade band — K-2 sized up further for legibility.
export const PRINT_TILING_BLANK_WIDTH = {
  [GRADE_BAND_K2]: 175,
  [GRADE_BAND_35]: 145,
};

// Tiling Columns' image-term word bank always stays to one row (unlike text
// terms, which reuse CHOICE_GRID_ROW_SIZES above and wrap to 2).
export const TILING_COLUMNS_IMAGE_CHIP_ROW_SIZES = {
  2: [2],
  3: [3],
  4: [4],
  5: [5],
  6: [6],
};

// Tiling Columns' image-chip size, per term count — each count has its own
// fixed size in the spec, not a shared/scaled one.
export const TILING_COLUMNS_IMAGE_CHIP_SIZE = {
  2: { width: 267, height: 152 },
  3: { width: 174, height: 113 },
  4: { width: 120, height: 80 },
  5: { width: 96, height: 64 },
  6: { width: 80, height: 53 },
};

// < 3 rendered lines uses maxFontSize, >= 3 steps down to minFontSize.
export const PRINT_INSTRUCTIONS_LINE_REDUCE_THRESHOLD = 3;

// In JS because TEIPrintInstructions measures rendered line count at these
// sizes. Name-block and footer values have no JS math, so they live in Stylus.
export const PRINT_TYPOGRAPHY = {
  [GRADE_BAND_K2]: {
    instructions: {
      maxFontSize: 18,
      minFontSize: 16,
    },
    // GO only: a fallback applied when an answer overflows its box.
    answers: {
      minFontSize: 16,
    },
  },
  [GRADE_BAND_35]: {
    instructions: {
      maxFontSize: 14,
      minFontSize: 12,
    },
    answers: {
      minFontSize: 12,
    },
  },
};

// Constructed Response answer text — confirmed Figma base size per grade band.
// Distinct from PRINT_TYPOGRAPHY[gradeBand].answers.minFontSize (GO's overflow
// fallback) since the two are confirmed to differ.
export const CONSTRUCTED_RESPONSE_ANSWER_FONT_SIZE = {
  [GRADE_BAND_K2]: 18,
  [GRADE_BAND_35]: 14,
};

// Flat, non-per-grade-band floor for the fit-then-grow loop below.
export const CONSTRUCTED_RESPONSE_ANSWER_MIN_FONT_SIZE = 10;

// Subtypes that get the 3px stroke; Venn and Bubble Map intentionally don't.
export const GO_SUBTYPES_WITH_STROKE = ['t_chart', 'frayer_model', 'table'];

// Connections card text; card images shrink the box available for text.
// Card text steps down from `base` / `withImage` to `min` to fit its lines.
export const CONNECTIONS_CARD_TEXT_FONT_SIZE = {
  [GRADE_BAND_K2]: { base: 18, withImage: 16, min: 12 },
  [GRADE_BAND_35]: { base: 14, withImage: 12, min: 10 },
};
